import React, { useCallback, useEffect, useState } from 'react'
import { Card, Space, Button, Form, Input, Modal, Image, type UploadProps, Upload, Select } from 'antd'
import Table, { type ColumnsType } from 'antd/es/table'
import { type AxiosError } from 'axios'
import { setLoader } from 'Store/slices/commonSlice'
import { useAppDispatch } from 'Hooks/ReduxHook'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { toast } from 'sonner'
import Http, { type ApiErrorData } from 'Util/Http'
import { HTTP_METHOD, type IAddNotification, type INotification } from 'Util/Interface'
import dayjs from 'dayjs'
import { ATTCHMENT_BASE_URL } from 'Util/Constant'

const Notification: React.FC = () => {
  const [tableData, setTableData] = useState<INotification[]>([])
  const [visible, setVisble] = useState(false)
  const [uploadImg, setUploadImg] = useState<any>('')

  const [form] = Form.useForm()

  const dispatch = useAppDispatch()

  const fetch = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      const response = await Http(`/notification`)
      if (response.data?.status && response?.data?.code === 200) {
        dispatch(setLoader(false))
        setTableData(response.data?.data)
      } else {
        setTableData([])
        dispatch(setLoader(false))
      }
    } catch (error: any) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
      setTableData([])
      dispatch(setLoader(false))
    }
  }, [dispatch])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const handleDelete = async (params: INotification) => {
    try {
      const response = await Http(`/notification/${String(params.id)}`, {
        method: 'DELETE',
      })
      if (response.data?.status && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        void fetch()
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleUpsert = async (param: IAddNotification) => {
    try {
      const formData = new FormData()
      formData.append('message', param?.message)
      formData.append('notificationType', param?.notificationType)
      formData.append('title', param?.title)

      if (param?.notificationImage && param?.notificationImage[0]?.originFileObj) {
        formData.append('notificationImage', param?.notificationImage[0]?.originFileObj)
      }

      const response = await Http({
        url: '/notification',
        method: HTTP_METHOD.POST,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.data?.status && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        form.resetFields()
        setVisble(false)
        setUploadImg('')
        void fetch()
      } else {
        toast.error(response?.data?.message ?? 'Something Went Wrong')
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleCancel = () => {
    setVisble(false)
    form.resetFields()
  }

  const columns: ColumnsType<INotification> = [
    {
      title: 'Id',
      dataIndex: 'index',
      key: 'id',
      render: (value, item, index) => (1 - 1) * 10 + index + 1,
    },
    {
      title: 'Notification Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Notification Message',
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: 'Notification Image',
      dataIndex: 'notificationImage',
      key: 'notificationImage',
      render: (value) => {
        return (
          <>
            {value ? (
              <Image src={`${ATTCHMENT_BASE_URL}${String(value)}`} alt="notification" height={100} width={100} />
            ) : (
              <div style={{ background: 'grey', height: 100, width: 100 }}></div>
            )}
          </>
        )
      },
    },
    {
      title: 'Notification Type',
      key: 'created_at',
      dataIndex: 'notificationType',
    },
    {
      title: 'Added Date',
      key: 'created_at',
      dataIndex: 'created_at',
      render: (value) => <>{dayjs(value).format('DD MMM YYYY')}</>,
    },
    {
      title: 'Action',
      dataIndex: 'actions',
      width: '15%',
      render: (_text, record: INotification) => {
        return (
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                void handleDelete(record)
              }}
            >
              Delete
            </Button>
          </Space>
        )
      },
    },
  ]

  const props: UploadProps = {
    accept: 'image/png, image/jpeg',
    name: 'thumbnail',
    // listType: 'picture-card',
    className: 'avatar-uploader',
    showUploadList: false,
    maxCount: 1,
    beforeUpload: (file) => {
      const type = file.type.split('/')[1]
      if (type === 'png' || type === 'jpeg' || type === 'jpg') {
        return true
      } else {
        toast.error('Invalid type of file, Accept Only Jpg and Png')
        return false
      }
    },
    customRequest: (info) => {
      setUploadImg(info?.file)
    },
  }

  return (
    <>
      <div className="content-wrapper">
        <Card
          className="card-wrapper"
          title={
            <div className="row justify-between">
              <div>
                <h3>Notification List</h3>
              </div>
              <div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisble(true)
                  }}
                >
                  Add Notifcation
                </Button>
              </div>
            </div>
          }
          style={{ padding: '1.25rem 1.25rem 0' }}
        >
          <Table columns={columns} dataSource={tableData} rowKey={(dataIndex: INotification) => dataIndex?.id} scroll={{ x: 800 }} bordered size="middle" />
        </Card>
      </div>
      <Modal
        title={'Add Notification'}
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Space key={'action'}>
            <Button type="primary" onClick={form.submit} className="btn-padding">
              Save
            </Button>
            <Button danger onClick={handleCancel}>
              Cancel
            </Button>
          </Space>,
        ]}
        width={600}
      >
        <Form
          form={form}
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 14 }}
          autoComplete="off"
          onFinish={(param: INotification) => {
            void handleUpsert(param)
          }}
          requiredMark="optional"
        >
          <Form.Item
            label="Notification Title"
            name="title"
            rules={[
              {
                required: true,
                message: 'Please Enter Notification Title!',
                type: 'string',
              },
            ]}
          >
            <Input className="custom-input" placeholder="Enter Notification Title" />
          </Form.Item>

          <Form.Item
            label="Notification Message"
            name="message"
            rules={[
              {
                required: true,
                message: 'Please Enter Notification Message!',
                type: 'string',
              },
            ]}
          >
            <Input className="custom-input" placeholder="Enter Notification Message" />
          </Form.Item>

          <Form.Item
            label="Notification Type"
            name="notificationType"
            rules={[
              {
                required: true,
                message: 'Select Notification Type!',
              },
            ]}
          >
            <Select
              placeholder="Select Notification Type"
              className="custom-select"
              options={[
                // { value: 'app_birthday', label: 'Birthday Notification' },
                // { value: 'app_notifications', label: 'Normal Notification' },
                // { value: 'app_emergency', label: 'Emergency Notification' },
                { value: 'app_event', label: 'App Notification' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Notification Image"
            name="notificationImage"
            rules={[
              {
                required: false,
                message: 'Please Notification Image!',
              },
            ]}
            className="custom-input-label mb-6"
            valuePropName="fileList"
            getValueFromEvent={(event) => {
              return event?.fileList
            }}
          >
            <Upload {...props}>{uploadImg?.name ? <Button>{String(uploadImg?.name)}</Button> : <Button> Upload Image </Button>}</Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default Notification
